import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftIcon, ArrowPathIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import Header from '../components/Header';
import FeedbackModal from '../components/FeedbackModal';
import { api, type ComponentDetail as ComponentDetailType, type ProductWithComponents } from '../services/api';
import { formatCurrency, formatHourlyWage } from '../utils/formatters';
import toast from 'react-hot-toast';

interface ComponentLevel {
  slug: string;
  name: string;
  component: ComponentDetailType | null;
}

const DynamicComponentDetail = () => {
  const { productSlug } = useParams<{ productSlug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [product, setProduct] = useState<ProductWithComponents | null>(null);
  const [componentPath, setComponentPath] = useState<ComponentLevel[]>([]);
  const [currentComponent, setCurrentComponent] = useState<ComponentDetailType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackType, setFeedbackType] = useState<'correction' | 'general_feedback'>('general_feedback');

  // Parse the URL path to extract component hierarchy
  const parseComponentPath = (pathname: string): string[] => {
    const match = pathname.match(/\/products\/[^/]+\/components\/(.+)/);
    if (!match) return [];
    
    const componentPart = match[1];
    const segments = componentPart.split('/');
    
    // Extract component slugs from the path
    // Format: component1/sub-components/component2/sub-components/component3
    const componentSlugs: string[] = [];
    for (let i = 0; i < segments.length; i += 2) {
      if (segments[i] && segments[i] !== 'sub-components') {
        componentSlugs.push(segments[i]);
      }
    }
    
    return componentSlugs;
  };

  // Build the component hierarchy by following the path
  const buildComponentHierarchy = async (slugs: string[]): Promise<ComponentLevel[]> => {
    const hierarchy: ComponentLevel[] = [];
    
    for (let i = 0; i < slugs.length; i++) {
      const slug = slugs[i];
      
      try {
        // Try to get component detail
        const componentData = await api.getComponentBySlug(slug);
        hierarchy.push({
          slug,
          name: componentData.name,
          component: componentData
        });
      } catch (error) {
        // If we can't get detailed data, try to find it in parent's sub-components
        if (i > 0 && hierarchy[i - 1].component) {
          const parentComponent = hierarchy[i - 1].component!;
          const subComponent = parentComponent.sub_components.find(
            sub => sub.slug === slug || sub.name.toLowerCase().replace(/\s+/g, '-') === slug
          );
          
          if (subComponent) {
            hierarchy.push({
              slug,
              name: subComponent.name,
              component: subComponent as ComponentDetailType
            });
          } else {
            throw new Error(`Sub-component ${slug} not found`);
          }
        } else {
          throw new Error(`Component ${slug} not found`);
        }
      }
    }
    
    return hierarchy;
  };

  // Load all data based on current URL
  const loadData = async (showRefreshToast = false) => {
    if (!productSlug) {
      setError('Product not found');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      // Parse the current path
      const slugs = parseComponentPath(location.pathname);
      if (slugs.length === 0) {
        setError('No component path found');
        setIsLoading(false);
        return;
      }
      
      // Load product data
      const productData = await api.getProductBySlug(productSlug, 'all');
      setProduct(productData);
      
      // Build component hierarchy
      const hierarchy = await buildComponentHierarchy(slugs);
      setComponentPath(hierarchy);
      
      // Set current component (last in hierarchy)
      const current = hierarchy[hierarchy.length - 1];
      setCurrentComponent(current.component);
      
      if (showRefreshToast) {
        toast.success('Data refreshed');
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load component data');
      if (!showRefreshToast) {
        toast.error('Failed to load component data');
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [productSlug, location.pathname]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadData(true);
  };

  // Navigate to a sub-component
  const navigateToSubComponent = (subComponentSlug: string) => {
    const currentPath = location.pathname;
    const newPath = `${currentPath}/sub-components/${subComponentSlug}`;
    navigate(newPath);
  };

  // Navigate back to parent component
  const navigateToParent = () => {
    if (componentPath.length <= 1) {
      // Navigate back to product
      navigate(`/products/${productSlug}`);
    } else {
      // Navigate to parent component
      const parentPath = componentPath.slice(0, -1);
      let path = `/products/${productSlug}/components/${parentPath[0].slug}`;
      
      for (let i = 1; i < parentPath.length; i++) {
        path += `/sub-components/${parentPath[i].slug}`;
      }
      
      navigate(path);
    }
  };

  // Generate breadcrumb
  const generateBreadcrumb = () => {
    const breadcrumbs = [
      { name: 'ProductTrace', path: '/' },
      { name: product?.display_name || 'Product', path: `/products/${productSlug}` }
    ];
    
    // Add component hierarchy to breadcrumb
    for (let i = 0; i < componentPath.length; i++) {
      const level = componentPath[i];
      let path = `/products/${productSlug}/components/${componentPath[0].slug}`;
      
      for (let j = 1; j <= i; j++) {
        path += `/sub-components/${componentPath[j].slug}`;
      }
      
      breadcrumbs.push({
        name: level.name,
        path: i === componentPath.length - 1 ? '' : path // Don't make current page clickable
      });
    }
    
    return breadcrumbs;
  };

  // Get component level (for display)
  const getComponentLevel = () => {
    if (componentPath.length === 1) return 'Component';
    return `Sub-Component (Level ${componentPath.length - 1})`;
  };

  // Get parent component name
  const getParentName = () => {
    if (componentPath.length <= 1) {
      return product?.display_name || 'Product';
    }
    return componentPath[componentPath.length - 2].name;
  };

  // Loading state
  if (isLoading && !isRefreshing) {
    return (
      <>
        <title>Loading Component - ProductTrace</title>
        <div className="min-h-screen bg-gray-50">
          <Header />
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
              <span className="ml-3 text-gray-600">Loading component details...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Error state
  if (error || !currentComponent || !product) {
    return (
      <>
        <title>Component Not Found - ProductTrace</title>
        <div className="min-h-screen bg-gray-50">
          <Header />
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">🔧</div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Component Not Found</h1>
              <p className="text-gray-600 mb-6">{error || 'The component you\'re looking for doesn\'t exist.'}</p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => navigate(`/products/${productSlug}`)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Back to Product
                </button>
                <button
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {isRefreshing ? (
                    <>
                      <div className="animate-spin w-4 h-4 mr-2 border-2 border-gray-400 border-t-transparent rounded-full" />
                      Refreshing...
                    </>
                  ) : (
                    <>
                      <ArrowPathIcon className="w-4 h-4 mr-2" />
                      Try Again
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  const breadcrumbs = generateBreadcrumb();

  return (
    <>
      <title>{currentComponent.name} in {product.display_name} - ProductTrace</title>
      <meta name="description" content={`Detailed analysis of ${currentComponent.name} component in ${product.display_name}`} />

      <div className="min-h-screen bg-gray-50">
        <Header />
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Back Button and Actions */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={navigateToParent}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back to {getParentName()}
            </button>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={() => {
                  setFeedbackType('correction');
                  setShowFeedbackModal(true);
                }}
                className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
              >
                <ChatBubbleLeftRightIcon className="w-4 h-4 mr-2" />
                Submit Correction
              </button>
              
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {isRefreshing ? (
                  <>
                    <div className="animate-spin w-4 h-4 mr-2 border-2 border-gray-400 border-t-transparent rounded-full" />
                    Refreshing...
                  </>
                ) : (
                  <>
                    <ArrowPathIcon className="w-4 h-4 mr-2" />
                    Refresh
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Dynamic Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-6">
            {breadcrumbs.map((crumb, index) => (
              <div key={index} className="flex items-center">
                {index > 0 && <span className="mr-2">&gt;</span>}
                {crumb.path ? (
                  <button onClick={() => navigate(crumb.path)} className="hover:text-gray-700">
                    {crumb.name}
                  </button>
                ) : (
                  <span className="text-gray-900 font-medium">{crumb.name}</span>
                )}
              </div>
            ))}
          </nav>

          {/* Component Header */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-3xl font-bold text-gray-900">
                {componentPath.length === 1 ? '🔧' : '⚙️'} {currentComponent.name}
              </h1>
              <div className="flex items-center space-x-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  componentPath.length === 1 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'bg-purple-100 text-purple-800'
                }`}>
                  {getComponentLevel()}
                </span>
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                  Product-Specific
                </span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {formatCurrency(currentComponent.estimated_cost_avg)}
                </div>
                <div className="text-sm text-gray-600">Estimated cost</div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {currentComponent.category || 'N/A'}
                </div>
                <div className="text-sm text-gray-600">Category</div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {currentComponent.complexity_level || 'N/A'}
                </div>
                <div className="text-sm text-gray-600">Complexity</div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  Level {componentPath.length}
                </div>
                <div className="text-sm text-gray-600">Component depth</div>
              </div>
            </div>
          </div>

          {/* Component Path Visualization */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">🗺️ Component Hierarchy</h2>
            <div className="flex items-center space-x-2 overflow-x-auto">
              {componentPath.map((level, index) => (
                <div key={index} className="flex items-center">
                  {index > 0 && (
                    <div className="text-gray-400 mx-2">→</div>
                  )}
                  <div className={`px-3 py-2 rounded-lg border ${
                    index === componentPath.length - 1
                      ? 'bg-blue-100 border-blue-300 text-blue-900 font-medium'
                      : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200 cursor-pointer'
                  }`}
                  onClick={() => {
                    if (index < componentPath.length - 1) {
                      let path = `/products/${productSlug}/components/${componentPath[0].slug}`;
                      for (let j = 1; j <= index; j++) {
                        path += `/sub-components/${componentPath[j].slug}`;
                      }
                      navigate(path);
                    }
                  }}
                  >
                    <div className="text-sm font-medium">{level.name}</div>
                    <div className="text-xs text-gray-500">
                      {index === 0 ? 'Component' : `Sub-Level ${index}`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Description */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">📋 Component Overview</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Description</h3>
                <p className="text-gray-700">{currentComponent.description || 'No description available.'}</p>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Function in {getParentName()}</h3>
                <p className="text-gray-700">{currentComponent.function || 'Function details not available.'}</p>
              </div>
            </div>
          </div>

          {/* Environmental Impact */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">🌱 Environmental Impact</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="text-sm text-green-600 font-medium">Carbon Footprint</div>
                <div className="text-lg font-bold text-green-900">
                  {currentComponent.environmental_impact?.carbon_footprint_kg ? `${currentComponent.environmental_impact.carbon_footprint_kg} kg CO₂` : 'N/A'}
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="text-sm text-blue-600 font-medium">Energy Use</div>
                <div className="text-lg font-bold text-blue-900">
                  {currentComponent.environmental_impact?.energy_consumption_kwh ? `${currentComponent.environmental_impact.energy_consumption_kwh} kWh` : 'N/A'}
                </div>
              </div>
              <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                <div className="text-sm text-cyan-600 font-medium">Water Usage</div>
                <div className="text-lg font-bold text-cyan-900">
                  {currentComponent.environmental_impact?.water_usage_liters ? `${currentComponent.environmental_impact.water_usage_liters} L` : 'N/A'}
                </div>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="text-sm text-orange-600 font-medium">Recyclability</div>
                <div className="text-lg font-bold text-orange-900">
                  {currentComponent.environmental_impact?.recyclability_percentage ? `${currentComponent.environmental_impact.recyclability_percentage}%` : 'N/A'}
                </div>
              </div>
            </div>
          </div>

          {/* Labor & Manufacturing */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">👥 Labor & Manufacturing</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="text-sm text-blue-600 font-medium">Workers Required</div>
                <div className="text-lg font-bold text-blue-900">
                  {currentComponent.labor_requirements?.worker_count ? `${currentComponent.labor_requirements.worker_count} people` : 'N/A'}
                </div>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="text-sm text-purple-600 font-medium">Skill Level</div>
                <div className="text-lg font-bold text-purple-900">
                  {currentComponent.labor_requirements?.skill_level || 'N/A'}
                </div>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="text-sm text-green-600 font-medium">Average Wage</div>
                <div className="text-lg font-bold text-green-900">
                  {formatHourlyWage(currentComponent.labor_requirements?.average_wage_usd_hour)}
                </div>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="text-sm text-yellow-600 font-medium">Manufacturing Time</div>
                <div className="text-lg font-bold text-yellow-900">
                  {currentComponent.manufacturing_time_days ? `${currentComponent.manufacturing_time_days} days` : 'N/A'}
                </div>
              </div>
            </div>
            
            {/* Working Conditions */}
            {currentComponent.labor_requirements?.working_conditions && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-2">Working Conditions</h3>
                <p className="text-sm text-gray-700">{currentComponent.labor_requirements.working_conditions}</p>
              </div>
            )}
          </div>

          {/* Production & Manufacturing - Combined View */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">🏭 Production & Manufacturing</h2>
            
            {/* Primary Country of Origin */}
            {currentComponent.country_of_origin && (
              <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">
                    {currentComponent.country_of_origin === 'China' ? '🇨🇳' :
                     currentComponent.country_of_origin === 'Taiwan' ? '🇹🇼' :
                     currentComponent.country_of_origin === 'South Korea' ? '🇰🇷' :
                     currentComponent.country_of_origin === 'Japan' ? '🇯🇵' :
                     currentComponent.country_of_origin === 'USA' || currentComponent.country_of_origin === 'United States' ? '🇺🇸' :
                     currentComponent.country_of_origin === 'Germany' ? '🇩🇪' : '🌍'}
                  </span>
                  <div>
                    <div className="font-medium text-purple-900">Primary Origin: {currentComponent.country_of_origin}</div>
                    <div className="text-sm text-purple-700">Main production and sourcing location</div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Production by Location with Processes */}
            {((currentComponent.locations && currentComponent.locations.length > 0) || 
              (currentComponent.production_locations && currentComponent.production_locations.length > 0)) ? (
              <div className="space-y-6">
                {(currentComponent.locations || currentComponent.production_locations || []).map((location, index) => {
                  // Find processes that happen at this location
                  const locationProcesses = currentComponent.processes?.filter(process => 
                    process.facility_locations?.some(fl => fl.country === location.country)
                  ) || [];
                  
                  return (
                    <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Location Header */}
                      <div className="bg-gray-50 p-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <span className="text-2xl mr-3">
                              {location.country === 'China' ? '🇨🇳' :
                               location.country === 'Taiwan' ? '🇹🇼' :
                               location.country === 'South Korea' ? '🇰🇷' :
                               location.country === 'Japan' ? '🇯🇵' :
                               location.country === 'USA' || location.country === 'United States' ? '🇺🇸' :
                               location.country === 'Germany' ? '🇩🇪' :
                               location.country === 'Vietnam' ? '🇻🇳' : '🌍'}
                            </span>
                            <div>
                              <div className="font-semibold text-lg text-gray-900">{location.country}</div>
                              <div className="text-sm text-gray-600">{location.description || 'Production facility'}</div>
                            </div>
                          </div>
                          {location.production_percentage && (
                            <div className="text-right">
                              <div className="text-2xl font-bold text-gray-900">{location.production_percentage}%</div>
                              <div className="text-sm text-gray-600">of production</div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="p-4">
                        {/* Companies */}
                        {location.companies && location.companies.length > 0 && (
                          <div className="mb-4">
                            <h4 className="font-medium text-gray-900 mb-2">🏢 Companies</h4>
                            <div className="flex flex-wrap gap-2">
                              {location.companies.map((company, idx) => (
                                <span key={idx} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                                  {company}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Manufacturing Processes at this Location */}
                        {locationProcesses.length > 0 && (
                          <div className="mb-4">
                            <h4 className="font-medium text-gray-900 mb-3">⚙️ Processes at this Location</h4>
                            <div className="space-y-3">
                              {locationProcesses.map((process, pidx) => {
                                const processAtLocation = process.facility_locations?.find(fl => fl.country === location.country);
                                
                                return (
                                  <div key={pidx} className="bg-blue-50 rounded-lg p-3">
                                    <div className="flex items-start justify-between mb-2">
                                      <div>
                                        <div className="font-medium text-gray-900 flex items-center">
                                          <span className="text-lg mr-2">
                                            {process.category === 'lithography' ? '🔬' :
                                             process.category === 'etching' ? '⚗️' :
                                             process.category === 'assembly' ? '🔧' :
                                             process.category === 'testing' ? '🧪' : '🏭'}
                                          </span>
                                          {process.name}
                                        </div>
                                        {process.description && (
                                          <div className="text-sm text-gray-600 mt-1">{process.description}</div>
                                        )}
                                      </div>
                                      {process.typical_duration_hours && (
                                        <div className="text-right ml-3">
                                          <div className="font-medium text-gray-900">{process.typical_duration_hours}h</div>
                                          <div className="text-xs text-gray-500">Duration</div>
                                        </div>
                                      )}
                                    </div>
                                    
                                    {/* Process Environmental Impact */}
                                    {(process.energy_consumption_kwh || process.water_usage_liters || process.carbon_footprint_kg) && (
                                      <div className="grid grid-cols-3 gap-2 mt-2">
                                        {process.energy_consumption_kwh && (
                                          <div className="text-center p-1 bg-white rounded">
                                            <div className="text-xs font-medium text-gray-900">{process.energy_consumption_kwh} kWh</div>
                                            <div className="text-xs text-gray-500">Energy</div>
                                          </div>
                                        )}
                                        {process.water_usage_liters && (
                                          <div className="text-center p-1 bg-white rounded">
                                            <div className="text-xs font-medium text-gray-900">{process.water_usage_liters} L</div>
                                            <div className="text-xs text-gray-500">Water</div>
                                          </div>
                                        )}
                                        {process.carbon_footprint_kg && (
                                          <div className="text-center p-1 bg-white rounded">
                                            <div className="text-xs font-medium text-gray-900">{process.carbon_footprint_kg} kg</div>
                                            <div className="text-xs text-gray-500">CO₂</div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    
                                    {/* Specific facilities for this process */}
                                    {processAtLocation && processAtLocation.percentage && (
                                      <div className="mt-2 text-sm text-blue-700">
                                        {processAtLocation.percentage}% of this process happens here
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        
                        {/* Facilities */}
                        {location.facilities && location.facilities.length > 0 && (
                          <div className="mb-4">
                            <h4 className="font-medium text-gray-900 mb-3">🏭 Facilities</h4>
                            <div className="space-y-2">
                              {location.facilities.map((facility, idx) => {
                                // Find which processes happen at this specific facility
                                const facilityProcesses = currentComponent.processes?.filter(process =>
                                  process.facility_locations?.some(fl => 
                                    fl.facility_name === facility.name || 
                                    (fl.company && facility.name.includes(fl.company))
                                  )
                                ) || [];
                                
                                return (
                                  <div key={idx} className="bg-gray-50 rounded-lg p-3">
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="font-medium text-gray-900">{facility.name}</div>
                                        {facility.city && (
                                          <div className="text-sm text-gray-600">{facility.city}</div>
                                        )}
                                        {facility.type && (
                                          <div className="text-xs text-gray-500">{facility.type}</div>
                                        )}
                                        {facility.address && (
                                          <div className="text-xs text-gray-600 mt-1">{facility.address}</div>
                                        )}
                                        
                                        {/* Show which processes happen at this facility */}
                                        {facilityProcesses.length > 0 && (
                                          <div className="mt-2">
                                            <span className="text-xs text-gray-500">Processes here: </span>
                                            {facilityProcesses.map((p, i) => (
                                              <span key={i} className="text-xs text-blue-600">
                                                {p.name}{i < facilityProcesses.length - 1 ? ', ' : ''}
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                      {facility.google_maps_link && (
                                        <a
                                          href={facility.google_maps_link}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="ml-3 px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors flex items-center"
                                        >
                                          📍 View on Map
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        
                        {/* Location Stats */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-100">
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-1">Labor</h4>
                            <div className="text-sm text-gray-600">
                              <div>Workers: {location.worker_count || currentComponent.labor_requirements?.worker_count || 'N/A'}</div>
                              <div>Wage: {formatHourlyWage(location.wage_usd_hour || currentComponent.labor_requirements?.average_wage_usd_hour)}</div>
                            </div>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-1">Environmental</h4>
                            <div className="text-sm text-gray-600">
                              <div>Energy: {location.renewable_energy_percentage || currentComponent.environmental_impact?.renewable_energy_percentage ? `${location.renewable_energy_percentage || currentComponent.environmental_impact?.renewable_energy_percentage}% renewable` : 'N/A'}</div>
                              <div>Waste: {location.waste_generation_kg || currentComponent.environmental_impact?.waste_generation_kg ? `${location.waste_generation_kg || currentComponent.environmental_impact?.waste_generation_kg}kg` : 'N/A'}</div>
                            </div>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-1">Why Here?</h4>
                            <div className="text-sm text-gray-600">
                              {location.why_here || location.description || 'Strategic production location'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {/* Processes not linked to specific locations */}
                {(() => {
                  const unlinkedProcesses = currentComponent.processes?.filter(process => 
                    !process.facility_locations || process.facility_locations.length === 0
                  ) || [];
                  
                  if (unlinkedProcesses.length > 0) {
                    return (
                      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <h4 className="font-medium text-gray-900 mb-3">⚙️ Additional Processes</h4>
                        <div className="text-sm text-gray-600 mb-3">
                          These processes don't have specific location data yet
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {unlinkedProcesses.map((process, idx) => (
                            <div key={idx} className="bg-white rounded p-3">
                              <div className="font-medium text-gray-900">{process.name}</div>
                              {process.description && (
                                <div className="text-sm text-gray-600">{process.description}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <div className="text-4xl mb-2">🌐</div>
                <div>No detailed production location data available</div>
                <div className="text-sm">Component analysis may still be in progress</div>
              </div>
            )}
          </div>

          {/* Sub-Components (Unlimited Levels) */}
          {currentComponent.sub_components && currentComponent.sub_components.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                🔧 Sub-Components (Level {componentPath.length + 1})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {currentComponent.sub_components.map((subComp) => (
                  <div 
                    key={subComp.id} 
                    className="p-4 bg-gray-50 border border-gray-200 rounded-lg hover:shadow-md transition-all cursor-pointer hover:border-blue-300"
                    onClick={() => navigateToSubComponent(subComp.slug || subComp.name.toLowerCase().replace(/\s+/g, '-'))}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium text-gray-900">{subComp.name}</div>
                      <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">
                        {subComp.category || 'Component'}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 mb-2">{subComp.description || 'Sub-component details'}</div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">
                        {formatCurrency(subComp.estimated_cost_avg)}
                      </span>
                      <span className="text-xs text-blue-600 font-medium">Click to explore →</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Feedback Modal */}
        <FeedbackModal
          isOpen={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          productId={product?.id}
          productName={product?.name}
          initialType={feedbackType}
        />
      </div>
    </>
  );
};

export default DynamicComponentDetail; 